import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Image as ImageIcon, X, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import useAccessibleOverlay from '@/hooks/useAccessibleOverlay';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * Public fleet photo gallery for a single vehicle.
 * Loads photo metadata first, then lazy-loads each thumbnail's data-url.
 */
export function PublicVehicleGallery({ companyId, vehicleId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // index

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/public/rentals/${companyId}/vehicles/${vehicleId}/photos`)
      .then((r) => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, vehicleId]);

  if (loading) {
    return <div className="text-sm text-slate-500 inline-flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Loading photos…</div>;
  }
  if (!items.length) {
    return null;
  }

  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3" data-testid={`pub-gallery-${vehicleId}`}>
        {items.map((p, idx) => (
          <PublicPhotoThumb
            key={p.id}
            companyId={companyId}
            vehicleId={vehicleId}
            photo={p}
            onClick={() => setLightbox(idx)}
            testId={`pub-photo-${p.id}`}
          />
        ))}
      </div>
      {lightbox != null && (
        <Lightbox
          companyId={companyId}
          vehicleId={vehicleId}
          items={items}
          index={lightbox}
          setIndex={setLightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
}

function PublicPhotoThumb({ companyId, vehicleId, photo, onClick, testId }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/rentals/${companyId}/vehicles/${vehicleId}/photos/${photo.id}/image`)
      .then((r) => { if (!cancelled) setUrl(r.data?.data_url || null); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [companyId, vehicleId, photo.id]);

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 group focus:outline-none focus:ring-2 focus:ring-[#1B4332]"
    >
      {url ? (
        <img src={url} alt={photo.caption || 'Vehicle photo'} className="w-full h-full object-cover group-hover:scale-105 transition" />
      ) : err ? (
        <div className="w-full h-full grid place-items-center text-slate-300"><ImageIcon className="w-5 h-5" /></div>
      ) : (
        <div className="w-full h-full grid place-items-center text-slate-300"><Loader2 className="w-4 h-4 animate-spin" /></div>
      )}
      {photo.is_cover && (
        <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-amber-500/95 text-white text-[9px] font-semibold rounded">Cover</span>
      )}
    </button>
  );
}

function Lightbox({ companyId, vehicleId, items, index, setIndex, onClose }) {
  const [url, setUrl] = useState(null);
  const current = items[index];
  const { panelRef } = useAccessibleOverlay(true, onClose);

  const fetchImg = useCallback(() => {
    setUrl(null);
    if (!current) return;
    axios.get(`${API}/public/rentals/${companyId}/vehicles/${vehicleId}/photos/${current.id}/image`)
      .then((r) => setUrl(r.data?.data_url || null))
      .catch(() => setUrl(null));
  }, [companyId, vehicleId, current]);

  useEffect(() => { fetchImg(); }, [fetchImg]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') setIndex((i) => (i > 0 ? i - 1 : items.length - 1));
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % items.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items.length, setIndex]);

  return (
    <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Vehicle photo viewer"
         className="fixed inset-0 z-[1000] bg-black/85 flex items-center justify-center p-4" data-testid="pub-lightbox"
         onClick={onClose}>
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="absolute top-4 right-4 p-2 rounded-full bg-white/15 hover:bg-white/30 text-white" data-testid="pub-lightbox-close" aria-label="Close">
        <X className="w-5 h-5" />
      </button>
      {items.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setIndex((i) => (i > 0 ? i - 1 : items.length - 1)); }}
                  className="absolute left-3 sm:left-6 p-2 rounded-full bg-white/15 hover:bg-white/30 text-white" data-testid="pub-lightbox-prev" aria-label="Previous">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setIndex((i) => (i + 1) % items.length); }}
                  className="absolute right-3 sm:right-6 p-2 rounded-full bg-white/15 hover:bg-white/30 text-white" data-testid="pub-lightbox-next" aria-label="Next">
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}
      <div className="max-w-4xl w-full max-h-[85vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        {url ? (
          <img src={url} alt={current?.caption || 'Vehicle photo'} className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl" />
        ) : (
          <div className="w-full aspect-video grid place-items-center text-white/70"><Loader2 className="w-8 h-8 animate-spin" /></div>
        )}
        {current?.caption && (
          <div className="mt-3 text-sm text-white/90 text-center max-w-2xl">{current.caption}</div>
        )}
        <div className="mt-2 text-xs text-white/60">{index + 1} / {items.length}</div>
      </div>
    </div>
  );
}

/**
 * Inline cover thumb for vehicle cards. Lazy-loaded with a graceful fallback.
 */
export function VehicleCoverThumb({ companyId, vehicleId, coverId, className = '' }) {
  const [url, setUrl] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!coverId) { setDone(true); return; }
    axios.get(`${API}/public/rentals/${companyId}/vehicles/${vehicleId}/photos/${coverId}/image`)
      .then((r) => { if (!cancelled) { setUrl(r.data?.data_url || null); setDone(true); } })
      .catch(() => { if (!cancelled) setDone(true); });
    return () => { cancelled = true; };
  }, [companyId, vehicleId, coverId]);

  return (
    <div className={`relative aspect-[16/10] w-full bg-slate-100 overflow-hidden ${className}`} data-testid={`vehicle-cover-${vehicleId}`}>
      {url ? (
        <img src={url} alt="Vehicle" className="w-full h-full object-cover" />
      ) : done ? (
        <div className="w-full h-full grid place-items-center text-slate-300">
          <ImageIcon className="w-8 h-8" />
        </div>
      ) : (
        <div className="w-full h-full grid place-items-center text-slate-300">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}
    </div>
  );
}

/**
 * Privacy-respecting business location banner. Renders nothing in hidden mode.
 */
export function PublicLocationBanner({ location }) {
  if (!location || location.mode === 'hidden' || !location.has_coordinates) {
    if (location?.city || location?.label || location?.service_area_text) {
      return (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm" data-testid="pub-location-text">
          <div className="font-semibold text-slate-900">Service area</div>
          <div className="mt-1 text-slate-700">
            {location.label || location.city}
            {location.service_area_text && (
              <span className="block text-xs text-slate-500 mt-1">{location.service_area_text}</span>
            )}
          </div>
          <div className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1">
            <Lock className="w-3 h-3" /> Exact pickup location shared after booking.
          </div>
        </div>
      );
    }
    return null;
  }
  return null;
}
